import { Loader2 } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'

import { Button } from '../Button'
import { openExternalBillingUrl } from '../../lib/authSessionPersistence'
import {
  createBillingPortalSession,
  fetchBillingEntitlements,
  type BillingEntitlements,
} from '../../lib/billing/api'
import { supabase } from '../../lib/supabase'

export default function ProfilePaymentTab() {
  const [loading, setLoading] = useState(true)
  const [entitlements, setEntitlements] = useState<BillingEntitlements | null>(null)
  const [portalLoading, setPortalLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await fetchBillingEntitlements()
      setEntitlements(data)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const openPortal = async () => {
    setPortalLoading(true)
    setError(null)
    try {
      const url = await createBillingPortalSession()
      await openExternalBillingUrl(url, supabase)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setPortalLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-text-secondary py-8" data-testid="profile-payment-tab">
        <Loader2 className="w-5 h-5 animate-spin" aria-hidden />
        <span>Loading payment settings…</span>
      </div>
    )
  }

  if (!entitlements?.billing_enabled) {
    return (
      <p className="text-body text-text-secondary py-4" data-testid="profile-payment-billing-disabled">
        Billing is not enabled on this environment yet.
      </p>
    )
  }

  const canOpenPortal = entitlements.has_stripe_customer === true

  return (
    <div className="space-y-6" data-testid="profile-payment-tab">
      {error && (
        <div className="rounded-lg border border-danger/20 bg-danger-subtle px-4 py-3 text-danger-strong text-body">
          {error}
        </div>
      )}
      <div className="rounded-lg border border-border-default bg-surface-default p-6 space-y-4">
        <h2 className="text-h3 font-semibold text-text-primary">Payment methods & invoices</h2>
        {canOpenPortal ? (
          <>
            <p className="text-body text-text-secondary">
              Manage cards, view invoices, and update billing details in the Stripe customer portal.
            </p>
            <Button
              type="button"
              variant="primary"
              primaryWeight="medium"
              disabled={portalLoading}
              data-testid="profile-payment-open-portal"
              onClick={() => void openPortal()}
            >
              {portalLoading ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
                  Opening portal…
                </span>
              ) : (
                'Open billing portal'
              )}
            </Button>
          </>
        ) : (
          <>
            <p className="text-body text-text-secondary" data-testid="profile-payment-no-customer">
              Billing portal opens after your first Pro checkout. Upgrade on the Subscription tab to
              add a payment method and view invoices.
            </p>
            <Button
              type="button"
              variant="primary"
              primaryWeight="medium"
              data-testid="profile-payment-go-subscription"
              onClick={() => {
                window.location.hash = 'subscription'
              }}
            >
              Go to Subscription
            </Button>
          </>
        )}
      </div>
    </div>
  )
}
